// 3rd party libraries
import React, { ReactElement, useMemo, useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import MaintenanceManagerIcon from 'icons/img/MaintenanceManagerIcon.svg';
import { Download } from 'icons/indexed-icons/Download';
// Components
import {
  Button,
  Flyout,
  Loader,
  MaintenanceEventDetail,
  PageHeader,
  TabNav,
  Typography
} from 'components';
import TopView, { TableFilterContainer } from './TopView';

// Types
import { Machine, Plant } from 'types';
import { CartListType } from 'types/parts/cart';
import {
  MaintenanceEvent,
  MaintenanceEventArgs,
  MaintenanceEventStatus,
  MaintenanceEventTableRow,
  MaintenanceFrequencyType,
  MaintenanceSchedule,
  MaintenanceScheduleArgs,
  MaintenanceScheduleTableRow,
  MaintenanceEventDownloadPayload,
  // Tab,
  MaintenanceCreator
} from 'types/maintenance';

// API
import { useGetPlantsQuery, useDownloadSelectedPlannedMaintenanceEventsMutation } from 'api';
import CreateServiceForm from './CreateServiceForm';
import CreateServiceBtn from 'components/MaintenanceTable/CreateServiceBtn';
// import DropdownTab from 'common/components/DropdownTab';
import { useHistory } from 'react-router-dom';
import SubTabs from 'components/SubTabs/SubTabs';
import FlagAlt from 'icons/indexed-icons/FlagAlt';
import History from 'icons/indexed-icons/History';
import Planned from 'icons/indexed-icons/Planned';
import { ToastMsg } from 'common/components/Toast';

interface Props {
  isExpanded: boolean;
}

const Container = styled.div<Props>`
  padding: 0rem 1.5rem;
  width: ${(props) => (props.isExpanded ? '100%' : '70%')};
`;

const ColumnContainer = styled.div`
  display: flex;
`;

const DownloadTypeSelectWrapper = styled.div`
  flex: 1;
  display: flex;
  margin-left: 1rem;
  & button {
    background-color: white;
    border: 0rem;
    margin-right: 1rem;
    box-shadow: none;
  }
  & span {
    margin-left: 0.6rem;
  }
`;
const ButtonWrapper = styled.div`
  margin: 0rem 1.5rem 0.2rem;
  padding: 41px auto;
`;
export enum TabViews {
  HighPriority,
  Planned,
  History
}
export enum SecondaryTabViews {
  RunTimeBased,
  Predictive,
  ServiceTech,
  PmPlanning,
  Reports,
  Resolved,
  Daily,
  Weekly,
  Monthly,
  Quarterly,
  HalfYearly,
  Yearly,
  OtherTasks,
}

const TabsContainer = styled.div`
  margin: 0 1.5rem;
  position: relative;
`;

export const addMachineDescToEvents = (
  events: MaintenanceEvent[],
  givenMachines: Machine[]
): MaintenanceEvent[] => {
  return events.map((ev) => {
    return {
      ...ev,
      machineDescription:
        givenMachines.find((m) => m.id === ev.machineId)?.description || 'Machine Unavailable'
    };
  });
};

export const addMachineDescToSchedule = (
  events: MaintenanceSchedule[],
  givenMachines: Machine[]
): MaintenanceSchedule[] => {
  if (events === undefined) return [];
  return events.map((ev) => {
    return {
      ...ev,
      machineDescription:
        givenMachines.find((m) => m.id === ev.machineId)?.description || 'Machine Unavailable'
    };
  });
};

interface MaintenanceServiceDashboardContentsProps {
  machineId?: string;
}
export interface MaintenanceSchedulePageProps {
  events: MaintenanceSchedule[];
  filter: MaintenanceScheduleArgs;
}
export default function MaintenanceServiceDashboardContents({
  machineId
}: MaintenanceServiceDashboardContentsProps): ReactElement {
  // TODO - Handle errors from retrieving plants

  const [downloadSelectedMaintenanceEvents, { isSuccess }] = useDownloadSelectedPlannedMaintenanceEventsMutation();
  const [refetchEventOnUpdate, setRefetchEventOnUpdate] = useState<number>(0);

  const secondaryTabArrayHighPriority = [
    {
      value: SecondaryTabViews.RunTimeBased,
      selected: true,
      label: 'run_time_based',
      slug: 'run-time-based',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.RunTimeBased);
          return history.replace(basePathMachine + `/${machineId}/high-priority/run-time-based`);
        }
        changeSecondaryTab(SecondaryTabViews.RunTimeBased);
        return history.replace(basePath + '/high-priority/run-time-based');
      },
      active: location.pathname.includes('/run-time-based')
    },
    {
      value: SecondaryTabViews.PmPlanning,
      selected: false,
      label: 'pm_planning',
      slug: 'pm-planning',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.PmPlanning);
          return history.replace(basePathMachine + `/${machineId}/high-priority/pm-planning`);
        }
        changeSecondaryTab(SecondaryTabViews.PmPlanning);
        return history.replace(basePath + '/high-priority/pm-planning');
      },
      active: location.pathname.includes('/pm-planning')
    },
    {
      value: SecondaryTabViews.Reports,
      selected: false,
      label: 'reports',
      slug: 'reports',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Reports);
          return history.replace(basePathMachine + `/${machineId}/high-priority/reports`);
        }
        changeSecondaryTab(SecondaryTabViews.Reports);
        return history.replace(basePath + '/high-priority/reports');
      },
      active: location.pathname.includes('/reports')
    },

    {
      value: SecondaryTabViews.Predictive,
      selected: false,
      label: 'predictive',
      slug: 'predictive',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Predictive);
          return history.replace(basePathMachine + `/${machineId}/high-priority/predictive`);
        }
        changeSecondaryTab(SecondaryTabViews.Predictive);
        return history.replace(basePath + '/high-priority/predictive');
      },
      active: location.pathname.includes('/predictive')
    },
    {
      value: SecondaryTabViews.ServiceTech,
      selected: false,
      label: 'service_tech',
      slug: 'service-tech',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.ServiceTech);
          return history.replace(basePathMachine + `/${machineId}/high-priority/service-tech`);
        }
        changeSecondaryTab(SecondaryTabViews.ServiceTech);
        return history.replace(basePath + '/high-priority/service-tech');
      },
      active: location.pathname.includes('/service-tech')
    }
  ];

  const secondaryTabArrayHistory = [
    {
      value: SecondaryTabViews.Resolved,
      selected: true,
      label: 'resolved',
      slug: 'resolved',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Resolved);
          return history.replace(basePathMachine + `/${machineId}/history/resolved`);
        }
        changeSecondaryTab(SecondaryTabViews.Resolved);
        return history.replace(basePath + '/history/resolved');
      },
      active: location.pathname.includes('/resolved')
    }
  ];

  const secondaryTabArrayPlanned = [
    {
      value: SecondaryTabViews.Daily,
      selected: true,
      label: 'daily',
      slug: 'daily',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Daily);
          return history.replace(basePathMachine + `/${machineId}/planned/daily`);
        }
        changeSecondaryTab(SecondaryTabViews.Daily);
        return history.replace(basePath + '/planned/daily');
      },
      active: location.pathname.includes('/daily')
    },
    {
      value: SecondaryTabViews.Weekly,
      selected: false,
      label: 'weekly',
      slug: 'weekly',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Weekly);
          return history.replace(basePathMachine + `/${machineId}/planned/weekly`);
        }
        changeSecondaryTab(SecondaryTabViews.Weekly);
        return history.replace(basePath + '/planned/weekly');
      },
      active: location.pathname.includes('/weekly')
    },
    {
      value: SecondaryTabViews.Monthly,
      selected: false,
      label: 'monthly',
      slug: 'monthly',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Monthly);
          return history.replace(basePathMachine + `/${machineId}/planned/monthly`);
        }
        changeSecondaryTab(SecondaryTabViews.Monthly);
        return history.replace(basePath + '/planned/monthly');
      },
      active: location.pathname.includes('/monthly')
    },
    {
      value: SecondaryTabViews.Quarterly,
      selected: false,
      label: 'quarterly',
      slug: 'quarterly',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Quarterly);
          return history.replace(basePathMachine + `/${machineId}/planned/quarterly`);
        }
        changeSecondaryTab(SecondaryTabViews.Quarterly);
        return history.replace(basePath + '/planned/quarterly');
      },
      active: location.pathname.includes('/quarterly')
    },
    {
      value: SecondaryTabViews.HalfYearly,
      selected: false,
      label: 'half_yearly',
      slug: 'half-yearly',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.HalfYearly);
          return history.replace(basePathMachine + `/${machineId}/planned/half-yearly`);
        }
        changeSecondaryTab(SecondaryTabViews.HalfYearly);
        return history.replace(basePath + '/planned/half-yearly');
      },
      active: location.pathname.includes('/half-yearly')
    },
    {
      value: SecondaryTabViews.Yearly,
      selected: false,
      label: 'yearly',
      slug: 'yearly',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.Yearly);
          return history.replace(basePathMachine + `/${machineId}/planned/yearly`);
        }
        changeSecondaryTab(SecondaryTabViews.Yearly);
        return history.replace(basePath + '/planned/yearly');
      },
      active: location.pathname.includes('/yearly')
    },
    {
      value: SecondaryTabViews.OtherTasks,
      selected: false,
      label: 'Other Tasks',
      slug: 'other',
      onClick: () => {
        if (machineId) {
          changeSecondaryTab(SecondaryTabViews.OtherTasks);
          return history.replace(basePathMachine + `/${machineId}/planned/others`);
        }
        changeSecondaryTab(SecondaryTabViews.OtherTasks);
        return history.replace(basePath + '/planned/others');
      },
      active: location.pathname.includes('/others')
    },
  ];

  const { t } = useTranslation(['fpns']);
  const history = useHistory();
  const [currentTab, setCurrentTab] = useState(TabViews.HighPriority);
  const [secondaryTab, setSecondaryTab] = useState(secondaryTabArrayHighPriority);
  const [isNewServiceEvent, setIsNewServiceEvent] = useState<boolean>(false);
  const [currentMaintenanceSchedulePage, setCurrentMaintenanceSchedulePage] =
    useState<MaintenanceSchedulePageProps>();
  // Handling the filters' visibility
  const [numResults, setNumResults] = useState<number | null>(null);

  // Define default filters for different tabs
  const activeSubtab = secondaryTab.find((sub) => sub.selected === true);
  const defaultFilter = useMemo(() => {
    const filter: MaintenanceEventArgs = {
      includeTasks: false,
      includeProductIds: false
    };
    filter.machineIds = machineId ? [machineId] : [];
    if (activeSubtab?.value === SecondaryTabViews.ServiceTech) {
      filter.creator = MaintenanceCreator.User;
    } else if (activeSubtab?.value === SecondaryTabViews.Predictive) {
      filter.creator = MaintenanceCreator.Predictive;
      filter.owner = 'Data Science';
    }
    return filter;
  }, [machineId, secondaryTab]);

  const defaultHistoryFilter = useMemo(() => {
    const filter: MaintenanceEventArgs = {
      includeTasks: false,
      includeProductIds: false,
      status: MaintenanceEventStatus.Completed
    };
    filter.machineIds = machineId ? [machineId] : [];
    return filter;
  }, [machineId]);

  const defaultPlannedFilter = useMemo(() => {
    const filter: MaintenanceScheduleArgs = {
      frequencyType: MaintenanceFrequencyType.Days,
      minimumFrequency: 1,
      maximumFrequency: 1
    };
    filter.machineIds = machineId ? [machineId] : [];
    return filter;
  }, [machineId]);

  // initialize filters to the default
  const [priorityFilter, setPriorityFilter] = useState<
    MaintenanceEventArgs | MaintenanceScheduleArgs
  >(defaultFilter);
  const [plannedFilter, setPlannedFilter] = useState<
    MaintenanceEventArgs | MaintenanceScheduleArgs
  >(defaultPlannedFilter);
  const [historyFilter, setHistoryFilter] = useState<
    MaintenanceEventArgs | MaintenanceScheduleArgs
  >(defaultHistoryFilter);
  // reset filters back to default on tab change
  const resetFilter = (thisCurrentTab: TabViews) => {
    if (thisCurrentTab === TabViews.HighPriority) {
      setPriorityFilter(defaultFilter);
      setSecondaryTab(secondaryTabArrayHighPriority);
    } else if (thisCurrentTab === TabViews.Planned) {
      setPlannedFilter(defaultPlannedFilter);
      setSecondaryTab(secondaryTabArrayPlanned);
    } else {
      setHistoryFilter(defaultHistoryFilter);
      setSecondaryTab(secondaryTabArrayHistory);
    }
  };
  const { data: plants, isLoading: plantsLoading } = useGetPlantsQuery();
  const machines = useMemo(() => {
    return plantsLoading
      ? undefined
      : plants?.reduce((acc: Machine[], plant: Plant) => acc.concat(plant?.machines ?? []), []);
  }, [plants, plantsLoading]);
  useEffect(() => {
    setPriorityFilter(defaultFilter);
    setPlannedFilter(defaultPlannedFilter);
    setHistoryFilter(defaultHistoryFilter);
  }, [defaultFilter, defaultHistoryFilter, defaultPlannedFilter, machines]);

  const [selectedPm, setSelectedPm] = useState<
    MaintenanceEventTableRow | null | MaintenanceScheduleTableRow
  >(null);
  const [completedMaintenanceEvents, setCompletedMaintenanceEvents] = useState<
    MaintenanceScheduleTableRow[]
  >([]);
  const [followUpPM, setFollowUpPM] = useState<MaintenanceEvent | null>(null);
  const [cartViewType, setCartViewType] = useState(CartListType.VIEW_TYPE_LESS);
  const [showFollowUpModal, setShowFollowUpModal] = useState<boolean>(false);
  const [showCreateServiceModal, setCreateServiceModal] = useState<boolean>(false);
  const flyoutWidth = cartViewType === CartListType.VIEW_TYPE_LESS ? '28.125rem' : '58.125rem';

  const flyoutCloseHandler = (maintenanceEvent?: MaintenanceEventTableRow) => {
    if (maintenanceEvent?.followUpNeeded) {
      setShowFollowUpModal(true);
      setFollowUpPM({ ...selectedPm } as MaintenanceEventTableRow);
    }
    setSelectedPm(null); //triggers closing of flyout
    setCartViewType(CartListType.VIEW_TYPE_LESS);
    changeTabAndRefreshEvents(currentTab, false);

    const update = refetchEventOnUpdate + 1;
    setRefetchEventOnUpdate(update);
  };

  const changeTabAndRefreshEvents = (tabView: TabViews, shouldResetFilter: boolean) => {
    setCurrentTab(tabView);
    if (shouldResetFilter) {
      resetFilter(tabView);
    }
  };

  const changeSecondaryTab = (secondaryTabView: SecondaryTabViews) => {
    const tempSecondaryTab = [
      ...secondaryTabArrayHighPriority,
      ...secondaryTabArrayPlanned,
      ...secondaryTabArrayHistory
    ].map((x) => {
      return {
        ...x,
        selected: x.value == secondaryTabView ? true : false
      };
    });
    setSecondaryTab(tempSecondaryTab);
  };

  const onModalClose = () => {
    setShowFollowUpModal(false);
    setCreateServiceModal(false);
    setFollowUpPM(null);
  };

  const submitClickHandler = () => {
    changeTabAndRefreshEvents(currentTab, true);
    setShowFollowUpModal(false);
    setCurrentTab(TabViews.HighPriority);

    if (machineId) {
      history.replace(basePathMachine + `/${machineId}/high-priority/service-tech`);
    } else {
      history.replace(basePath + '/high-priority/service-tech');
    }
    setIsNewServiceEvent(true);
    changeSecondaryTab(SecondaryTabViews.ServiceTech);
  };
  const getSelectedServiceData = (data: MaintenanceEventTableRow) => {
    setShowFollowUpModal(true);
    setCreateServiceModal(true);
    setFollowUpPM({ ...data });
  };

  const initiateDownload = (type: string): void => {
    const downloadPayload: MaintenanceEventDownloadPayload = { machineIds: [] };
    if (machineId) {
      downloadPayload.machineIds?.push(machineId as string);
    }
    // Download maintenance events
    if (type === 'downloadselected') {
      if (completedMaintenanceEvents && completedMaintenanceEvents.length > 0) {
        downloadPayload.maintenanceScheduleIds = completedMaintenanceEvents.map(
          (event) => event.id
        );
        downloadPayload.maximumFrequency = currentMaintenanceSchedulePage?.filter.minimumFrequency;
        downloadPayload.maximumFrequency = currentMaintenanceSchedulePage?.filter.maximumFrequency;

        downloadSelectedMaintenanceEvents(downloadPayload)
          .unwrap()
          .then((result) => {
            setCompletedMaintenanceEvents([]);
            window.location.href = `${result.url}`;
          })
          .catch((error) => {
            ToastMsg({
              heading: 'Error',
              type: 'error',
              message: 'Error occurred while downloading events'
            });
            console.error(error);
          });
      } else {
        ToastMsg({ heading: 'Error', type: 'error', message: 'No events have been selected!' });
      }
    } else {
      downloadPayload.minimumFrequency = currentMaintenanceSchedulePage?.filter.minimumFrequency;
      downloadPayload.maximumFrequency = currentMaintenanceSchedulePage?.filter.maximumFrequency;
      downloadSelectedMaintenanceEvents(downloadPayload)
        .unwrap()
        .then((result) => {
          window.location.href = `${result.url}`;
        })
        .catch((error) => {
          ToastMsg({
            heading: 'Error',
            type: 'error',
            message: 'Error occurred while downloading events'
          });
          console.error(error);
        });
    }
  };
  const changeTab = useCallback((tab: SecondaryTabViews) => {
    changeSecondaryTab(tab);
  }, []);

  const basePathMachine = '/maintenance/servicedashboard/machine';
  const basePath = '/maintenance/servicedashboard';

  const MainTabs = [
    {
      icon: <FlagAlt />,
      label:
        numResults === null || currentTab != TabViews.HighPriority
          ? t('maintenance_events')
          : t('maintenance_events'),
      onClick: () => {
        if (machineId) {
          setCurrentTab(TabViews.HighPriority);
          resetFilter(TabViews.HighPriority);
          return history.replace(
            basePathMachine + `/${machineId}/high-priority/${secondaryTabArrayHighPriority[0].slug}`
          );
        }
        setCurrentTab(TabViews.HighPriority);
        resetFilter(TabViews.HighPriority);
        history.replace(basePath + `/high-priority/${secondaryTabArrayHighPriority[0].slug}`);
      },
      isTabEnabled: true,
      active: location.pathname.includes('/high-priority')
    },
    {
      icon: <Planned />,
      label:
        numResults === null || currentTab != TabViews.Planned
          ? t('maintenance_tasks')
          : t('maintenance_tasks'),
      onClick: () => {
        if (machineId) {
          setCurrentTab(TabViews.Planned);
          resetFilter(TabViews.Planned);
          return history.replace(
            basePathMachine + `/${machineId}/planned/${secondaryTabArrayPlanned[0].slug}`
          );
        }
        setCurrentTab(TabViews.Planned);
        resetFilter(TabViews.Planned);

        history.replace(basePath + `/planned/${secondaryTabArrayPlanned[0].slug}`);
      },
      active: location.pathname.includes('/planned'),
      isTabEnabled: true
    },
    {
      icon: <History />,
      label:
        numResults === null || currentTab != TabViews.History
          ? t('completed_tasks')
          : t('completed_tasks'),
      onClick: () => {
        if (machineId) {
          setCurrentTab(TabViews.History);
          resetFilter(TabViews.History);
          return history.replace(
            basePathMachine + `/${machineId}/history/${secondaryTabArrayHistory[0].slug}`
          );
        }
        setCurrentTab(TabViews.History);
        resetFilter(TabViews.History);
        history.replace(basePath + `/history/${secondaryTabArrayHistory[0].slug}`);
      },
      isTabEnabled: true,
      active: location.pathname.includes('/history')
    }
  ];

  const SubTabItems = useMemo(() => {
    if (currentTab === TabViews.Planned) return secondaryTabArrayPlanned;
    if (currentTab === TabViews.History) return secondaryTabArrayHistory;
    return secondaryTabArrayHighPriority;
  }, [currentTab, secondaryTab]);

  React.useLayoutEffect(() => {
    const path = location.pathname;
    if (path.includes('/history')) {
      setCurrentTab(TabViews.History);
      return changeSecondaryTab(SecondaryTabViews.Resolved);
    } else if (path.includes('/planned')) {
      setCurrentTab(TabViews.Planned);
      if (path.includes('/weekly')) return changeSecondaryTab(SecondaryTabViews.Weekly);
      if (path.includes('/monthly')) return changeSecondaryTab(SecondaryTabViews.Monthly);
      if (path.includes('/quarterly')) return changeSecondaryTab(SecondaryTabViews.Quarterly);
      if (path.includes('/yearly')) return changeSecondaryTab(SecondaryTabViews.Yearly);
      if (path.includes('/half-yearly')) return changeSecondaryTab(SecondaryTabViews.HalfYearly);
      if (path.includes('/others')) return changeSecondaryTab(SecondaryTabViews.OtherTasks);
      return changeSecondaryTab(SecondaryTabViews.Daily);
    } else {
      setCurrentTab(TabViews.HighPriority);
      if (path.includes('/reports')) return changeSecondaryTab(SecondaryTabViews.Reports);
      if (path.includes('/pm-planning')) return changeSecondaryTab(SecondaryTabViews.PmPlanning);
      if (path.includes('/predictive')) return changeSecondaryTab(SecondaryTabViews.Predictive);
      if (path.includes('/service-tech')) return changeSecondaryTab(SecondaryTabViews.ServiceTech);
      return changeSecondaryTab(SecondaryTabViews.RunTimeBased);
    }
  }, []);
  return (
    <>
      <PageHeader
        mb="2rem"
        heading={t('card_maintenance_manager', { ns: 'common' })}
        icon={MaintenanceManagerIcon}
      />
      {!machines ? (
        <Loader />
      ) : (
        <>
          <TabsContainer>
            <TabNav items={MainTabs} />
            <SubTabs tabs={SubTabItems} machineId={machineId} />
          </TabsContainer>

          {currentTab === TabViews.Planned && machines && (
            <ButtonWrapper>
              <TableFilterContainer mb="0" padding="17px 0.5rem">
                {/* <PermissionWrapper
                  page={PermissionScopeName.MAINTENANCE_MANAGER}
                  scope={UserScopes.Write}
                > */}
                <CreateServiceBtn handleServiceModal={() => setCreateServiceModal(true)} />
                {/* </PermissionWrapper> */}
                <DownloadTypeSelectWrapper>
                  <Button
                    icon={<Download />}
                    variant={'text'}
                    onClick={() => initiateDownload('downloadselected')}
                  >
                    <span>{t('download')}</span>
                  </Button>
                </DownloadTypeSelectWrapper>
              </TableFilterContainer>
            </ButtonWrapper>
          )}

          <ColumnContainer>
            <Container isExpanded>
              {currentTab === TabViews.HighPriority && machines && (
                <TopView
                  filter={priorityFilter}
                  machines={machines}
                  setSelectedPm={setSelectedPm}
                  setNumResults={setNumResults}
                  secondaryTab={secondaryTab}
                  primaryTab={currentTab}
                  onClickCreateService={getSelectedServiceData}
                  handleCreateServiceModal={setCreateServiceModal}
                  handleTabChange={changeTab}
                  refetchEventOnUpdate={refetchEventOnUpdate}
                  isNewServiceEvent={isNewServiceEvent}
                  setIsNewServiceEvent={setIsNewServiceEvent}
                />
              )}
              {currentTab === TabViews.Planned && machines && (
                <TopView
                  filter={plannedFilter}
                  machines={machines}
                  setSelectedPm={setSelectedPm}
                  setNumResults={setNumResults}
                  secondaryTab={secondaryTab}
                  primaryTab={currentTab}
                  updateCompletedEvents={setCompletedMaintenanceEvents}
                  isDownloaded={isSuccess}
                  setCurrentMaintenanceSchedulePage={setCurrentMaintenanceSchedulePage}
                />
              )}
              {currentTab === TabViews.History && machines && (
                <TopView
                  filter={historyFilter}
                  machines={machines}
                  setSelectedPm={setSelectedPm}
                  setNumResults={setNumResults}
                  secondaryTab={secondaryTab}
                  primaryTab={currentTab}
                  onClickCreateService={getSelectedServiceData}
                  handleCreateServiceModal={setCreateServiceModal}
                />
              )}
            </Container>
          </ColumnContainer>

          {selectedPm &&
            (typeof selectedPm.machineDescription == 'string' ||
              typeof selectedPm.machineDescription == 'undefined') && (
              <Flyout width={flyoutWidth} visible={!!selectedPm} onClose={flyoutCloseHandler}>
                {selectedPm && (
                  <MaintenanceEventDetail
                    maintenanceEventId={selectedPm.id}
                    machineDescription={selectedPm.machineDescription}
                    cartViewType={cartViewType}
                    onCartViewType={setCartViewType}
                    onClose={flyoutCloseHandler}
                  />
                )}
              </Flyout>
            )}
          {showCreateServiceModal && (
            <Flyout width={'28rem'} visible={showCreateServiceModal} onClose={() => onModalClose()}>
              {showCreateServiceModal && (
                <CreateServiceForm
                  title={FollowUpModal(showFollowUpModal, t)}
                  followUpPM={followUpPM}
                  handleClose={onModalClose}
                  submitClickHandler={submitClickHandler}
                  machines={showCreateServiceModal ? machines : []}
                  machineId={machineId}
                  showCreateServiceModal={showCreateServiceModal ? true : false}
                />
              )}
            </Flyout>
          )}
        </>
      )}
    </>
  );
}
function FollowUpModal(
  showFollowUpModal: boolean,
  t: TFunction<'fpns'[], undefined>
): React.ReactNode {
  return showFollowUpModal ? (
    <Typography mb={0} size={'1.5rem'} weight="bold">
      <span>{t('create_follow_up_service')}</span>{' '}
    </Typography>
  ) : (
    <Typography mb={0} size={'1.5rem'} weight="bold">
      <span>{t('create_new_service')}</span>{' '}
    </Typography>
  );
}
